import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Grid, List, Filter, Search, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useCart } from "@/hooks/useCartSync";
import { formatPriceWithCurrency } from "@/utils/currency";
import { useCurrency } from "@/hooks/useCurrency";
import DOMPurify from 'dompurify';
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { useHaptics } from "@/hooks/useHaptics";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { DealBadge } from "@/components/DealBadge";
import { useProductDeal } from "@/hooks/useProductDeal";
import { ProductRating } from "@/components/ProductRating";
import { useCategoryTranslation, useAllCategories } from "@/hooks/useCategoryTranslation";
import { useLanguage } from "@/hooks/useLanguage";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

// Helper function to sanitize HTML content
const sanitizeHTML = (html: string | null | undefined): string => {
  if (!html) return '';
  return DOMPurify.sanitize(html);
};

const ITEMS_PER_PAGE = 20;

interface Product {
  id: string;
  title: string;
  price_cents: number;
  currency: string;
  images: string[] | null;
  vendor_id: string;
  stock: number;
  active: boolean;
  description: string | null;
  category: string | null;
  brand: string | null;
  average_rating?: number;
  review_count?: number;
}

const AllProducts = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { displayCurrency, convertPrice } = useCurrency();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [selectedBrand, setSelectedBrand] = useState(searchParams.get('brand') || 'all');
  const [priceRange, setPriceRange] = useState(searchParams.get('priceRange') || 'all');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'newest');
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [brands, setBrands] = useState<string[]>([]);
  const { addItem } = useCart();
  const { impact, notification } = useHaptics();
  const { language } = useLanguage();
  
  // Charger toutes les catégories avec traductions
  const { categories: allCategories, loading: categoriesLoading } = useAllCategories();

  // Product Card Component with deal support
  const ProductCardItem = ({ product }: { product: Product }) => {
    const { deal, calculateDiscountedPrice } = useProductDeal(product.id);
    const { categoryName } = useCategoryTranslation(product.category);

    return (
      <Card 
        className={`group cursor-pointer hover:shadow-elegant transition-all duration-300 ${
          viewMode === "grid" ? "hover:-translate-y-1" : ""
        }`}
        onClick={() => handleProductClick(product.id)}
      >
        {viewMode === "grid" ? (
          <>
            <div className="relative aspect-square overflow-hidden rounded-t-lg">
              <img
                src={product.images?.[0] || '/placeholder.svg'}
                alt={product.title}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute top-3 left-3">
                <Badge variant="secondary">
                  {categoryName || product.category}
                </Badge>
              </div>
              {product.stock <= 5 && (
                <div className="absolute top-3 left-3" style={{ marginTop: '36px' }}>
                  <Badge variant="destructive">
                    Stock: {product.stock}
                  </Badge>
                </div>
              )}
              <div className="absolute top-3 right-3">
                <DealBadge productId={product.id} />
              </div>
            </div>
            <CardContent className="p-4">
              <h3 className="font-semibold text-lg mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                {product.title}
              </h3>
              <ProductRating 
                rating={product.average_rating || 0} 
                reviewCount={product.review_count || 0}
                size="sm"
              />
              {product.brand && (
                <p className="text-xs text-muted-foreground mb-1 mt-1">
                  Marque: {product.brand}
                </p>
              )}
              <div 
                className="text-sm text-muted-foreground mb-3 line-clamp-2"
                dangerouslySetInnerHTML={{ __html: sanitizeHTML(product.description || '') }}
              />
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  {deal ? (
                    <>
                      <span className="text-xl font-bold text-primary">
                        {formatPriceWithCurrency(
                          calculateDiscountedPrice(product.price_cents),
                          product.currency,
                          displayCurrency,
                          convertPrice
                        )}
                      </span>
                      <span className="text-sm text-muted-foreground line-through">
                        {formatPriceWithCurrency(product.price_cents, product.currency, displayCurrency, convertPrice)}
                      </span>
                    </>
                  ) : (
                    <span className="text-xl font-bold text-primary">
                      {formatPriceWithCurrency(product.price_cents, product.currency, displayCurrency, convertPrice)}
                    </span>
                  )}
                </div>
                <Button
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddToCart(product);
                  }}
                  disabled={product.stock <= 0}
                >
                  Ajouter
                </Button>
              </div>
            </CardContent>
          </>
        ) : (
          <CardContent className="p-4">
            <div className="flex gap-4">
              <div className="relative w-24 h-24 flex-shrink-0">
                <img
                  src={product.images?.[0] || '/placeholder.svg'}
                  alt={product.title}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover rounded-lg"
                />
                <div className="absolute -top-1 -right-1">
                  <DealBadge productId={product.id} className="scale-75" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-semibold text-lg line-clamp-1 group-hover:text-primary transition-colors">
                      {product.title}
                    </h3>
                    <ProductRating 
                      rating={product.average_rating || 0} 
                      reviewCount={product.review_count || 0}
                      size="sm"
                    />
                    {product.brand && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Marque: {product.brand}
                      </p>
                    )}
                  </div>
                  <Badge variant="secondary" className="ml-2">
                    {categoryName || product.category}
                  </Badge>
                </div>
                <div 
                  className="text-sm text-muted-foreground mb-3 line-clamp-2"
                  dangerouslySetInnerHTML={{ __html: sanitizeHTML(product.description || '') }}
                />
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    {deal ? (
                      <>
                        <span className="text-lg font-bold text-primary">
                          {formatPriceWithCurrency(
                            calculateDiscountedPrice(product.price_cents),
                            product.currency,
                            displayCurrency,
                            convertPrice
                          )}
                        </span>
                        <span className="text-sm text-muted-foreground line-through">
                          {formatPriceWithCurrency(product.price_cents, product.currency, displayCurrency, convertPrice)}
                        </span>
                      </>
                    ) : (
                      <span className="text-lg font-bold text-primary">
                        {formatPriceWithCurrency(product.price_cents, product.currency, displayCurrency, convertPrice)}
                      </span>
                    )}
                  </div>
                  <Button
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddToCart(product);
                    }}
                    disabled={product.stock <= 0}
                  >
                    Ajouter au panier
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>
    );
  };

  const handleRefresh = async () => {
    setCurrentPage(0);
    setHasMore(true);
    await fetchProducts(true);
  };

  const { containerRef, isPulling, pullDistance, isRefreshing, shouldShowIndicator } = usePullToRefresh({
    onRefresh: handleRefresh,
    threshold: 80
  });

  const { observerTarget } = useInfiniteScroll({
    onLoadMore: () => fetchProducts(false, currentPage + 1),
    hasMore,
    isLoading: loadingMore
  });

  useEffect(() => {
    fetchBrands();
    handleRefresh();
  }, [searchTerm, selectedCategory, selectedBrand, priceRange, sortBy]);

  useEffect(() => {
    // Update URL params
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (selectedCategory !== 'all') params.set('category', selectedCategory);
    if (selectedBrand !== 'all') params.set('brand', selectedBrand);
    if (priceRange !== 'all') params.set('priceRange', priceRange);
    if (sortBy !== 'newest') params.set('sort', sortBy);
    setSearchParams(params);
  }, [searchTerm, selectedCategory, selectedBrand, priceRange, sortBy]);

  const fetchBrands = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('brand')
        .eq('active', true);

      if (error) throw error;

      const uniqueBrands = [...new Set(data?.map(p => p.brand).filter(Boolean))] as string[];
      setBrands(uniqueBrands);
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const fetchProducts = async (isRefresh = false, page = 0) => {
    try {
      if (isRefresh) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const from = page * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;

      let query = supabase
        .from('products')
        .select(`
          *,
          vendor:profiles!vendor_id(vendor_status)
        `, { count: 'exact' })
        .eq('active', true)
        .range(from, to);

      // Apply search filter
      if (searchTerm) {
        query = query.or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,brand.ilike.%${searchTerm}%`);
      }

      // Apply category filter
      if (selectedCategory !== 'all') {
        query = query.eq('category', selectedCategory);
      }

      // Apply brand filter
      if (selectedBrand !== 'all') {
        query = query.eq('brand', selectedBrand);
      }

      // Apply sorting
      switch (sortBy) {
        case 'price_low':
          query = query.order('price_cents', { ascending: true });
          break;
        case 'price_high':
          query = query.order('price_cents', { ascending: false });
          break;
        case 'name':
          query = query.order('title', { ascending: true });
          break;
        default:
          query = query.order('created_at', { ascending: false });
      }

      const { data, error, count } = await query;

      if (error) throw error;
      
      // Filter products to only show those from approved vendors
      let filteredData = (data || [])
        .filter((product: any) => product.vendor?.vendor_status === 'approved');
      
      // Apply price range filter on client side (after conversion if needed)
      if (priceRange !== 'all') {
        const ranges: { [key: string]: [number, number] } = {
          '0-1000': [0, 100000],
          '1000-5000': [100000, 500000],
          '5000-10000': [500000, 1000000],
          '10000-50000': [1000000, 5000000],
          '50000+': [5000000, Infinity]
        };
        
        const [min, max] = ranges[priceRange] || [0, Infinity];
        filteredData = filteredData.filter(p => p.price_cents >= min && p.price_cents < max);
      }
      
      // Fetch ratings for each product
      const productsWithRatings = await Promise.all(
        filteredData.map(async (product: any) => {
          try {
            const [ratingResult, countResult] = await Promise.all([
              supabase.rpc('get_product_average_rating', { product_uuid: product.id }),
              supabase.rpc('get_product_review_count', { product_uuid: product.id })
            ]);
            
            return {
              ...product,
              average_rating: ratingResult.data,
              review_count: countResult.data
            };
          } catch (error) {
            console.error('Error fetching ratings for product:', product.id, error);
            return {
              ...product,
              average_rating: 0,
              review_count: 0
            };
          }
        })
      );
      
      if (isRefresh || page === 0) {
        setProducts(productsWithRatings);
      } else {
        setProducts(prev => [...prev, ...productsWithRatings]);
      }

      setCurrentPage(page);
      setHasMore(filteredData.length === ITEMS_PER_PAGE);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const formatPrice = (cents: number, currency: string) => {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  };

  const handleAddToCart = async (product: Product) => {
    await impact('medium');
    addItem({
      id: product.id,
      title: product.title,
      price_cents: product.price_cents,
      currency: product.currency,
      images: product.images || [],
      vendor_id: product.vendor_id
    });
    await notification('success');
  };

  const handleProductClick = (productId: string) => {
    navigate(`/product/${productId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-1/4"></div>
            <div className="h-6 bg-muted rounded w-1/3"></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(12)].map((_, i) => (
                <Card key={i}>
                  <div className="aspect-square bg-muted"></div>
                  <CardContent className="p-4 space-y-3">
                    <div className="h-4 bg-muted rounded"></div>
                    <div className="h-3 bg-muted rounded w-3/4"></div>
                    <div className="h-4 bg-muted rounded w-1/2"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container py-8" ref={containerRef}>
        {/* Pull to Refresh Indicator */}
        {shouldShowIndicator && (
          <div className="fixed top-16 left-0 right-0 z-50 flex justify-center">
            <div className="bg-primary text-primary-foreground px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span className="text-sm">
                {isRefreshing ? 'Actualisation...' : 'Relâchez pour actualiser'}
              </span>
            </div>
          </div>
        )}
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour à l'accueil
          </Button>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-4">Tous nos produits</h1>
          <p className="text-muted-foreground">
            Découvrez notre gamme complète de {products.length} produits
          </p>
        </div>

        {/* Filters */}
        <div className="space-y-4 mb-8">
          <div className="grid md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher des produits..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger>
                <SelectValue placeholder="Catégorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les catégories</SelectItem>
                {allCategories.map((category) => (
                  <SelectItem key={category.slug} value={category.slug}>
                    {category.name_translations?.[language as 'fr' | 'en' | 'ln' | 'sw'] || category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Plus récent</SelectItem>
                <SelectItem value="price_low">Prix croissant</SelectItem>
                <SelectItem value="price_high">Prix décroissant</SelectItem>
                <SelectItem value="name">Nom A-Z</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex border rounded-lg">
              <Button
                variant={viewMode === "grid" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("grid")}
              >
                <Grid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "list" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("list")}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {/* Additional filters row */}
          <div className="grid md:grid-cols-3 gap-4">
            <Select value={selectedBrand} onValueChange={setSelectedBrand}>
              <SelectTrigger>
                <SelectValue placeholder="Marque" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les marques</SelectItem>
                {brands.map((brand) => (
                  <SelectItem key={brand} value={brand}>
                    {brand}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={priceRange} onValueChange={setPriceRange}>
              <SelectTrigger>
                <SelectValue placeholder="Tranche de prix" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les prix</SelectItem>
                <SelectItem value="0-1000">0 - 10 CDF</SelectItem>
                <SelectItem value="1000-5000">10 - 50 CDF</SelectItem>
                <SelectItem value="5000-10000">50 - 100 CDF</SelectItem>
                <SelectItem value="10000-50000">100 - 500 CDF</SelectItem>
                <SelectItem value="50000+">500+ CDF</SelectItem>
              </SelectContent>
            </Select>

            <Button 
              variant="outline" 
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('all');
                setSelectedBrand('all');
                setPriceRange('all');
              }}
            >
              <Filter className="h-4 w-4 mr-2" />
              Réinitialiser les filtres
            </Button>
          </div>
        </div>

        {/* Results count */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-muted-foreground">
            {products.length} produit{products.length !== 1 ? 's' : ''} trouvé{products.length !== 1 ? 's' : ''}
            {searchTerm && ` pour "${searchTerm}"`}
            {selectedCategory !== 'all' && ` dans ${selectedCategory}`}
            {selectedBrand !== 'all' && ` de la marque ${selectedBrand}`}
            {priceRange !== 'all' && ` dans la tranche de prix sélectionnée`}
          </p>
        </div>

        {/* Products Grid/List */}
        {products.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-lg text-muted-foreground mb-4">
              Aucun produit trouvé.
            </p>
            <Button onClick={() => {
              setSearchTerm('');
              setSelectedCategory('all');
            }}>
              Effacer les filtres
            </Button>
          </div>
        ) : (
          <div className={
            viewMode === "grid" 
              ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
              : "space-y-4"
          }>
            {products.map((product) => (
              <ProductCardItem key={product.id} product={product} />
            ))}
          </div>
        )}

        {/* Infinite Scroll Observer */}
        {hasMore && !loading && products.length > 0 && (
          <div ref={observerTarget} className="py-8 flex justify-center">
            {loadingMore && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Chargement...</span>
              </div>
            )}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default AllProducts;